// src/components/widgets/PulsoPublicidadWidget.js
// Widget "Pulso de Publicidad" 📊 — resumen ejecutivo del gasto publicitario
// (Meta + TikTok) + checklist automático de optimización con semáforo.
// Lee 1 doc precalculado (`widgets_datos/pulso_publicidad`, lo arma el cron
// cronPulsoPublicidad 3×/día con datos YA persistidos — sin APIs externas).
// Se muestra por PERMISO (`widget-ads-insights`, sección WIDGETS de Permisos).
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { MegaphoneIcon, ClockIcon, ChartBarIcon, BuildingOfficeIcon, CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon, ChevronDownIcon, ArrowTrendingUpIcon } from '../ui/Icons';
import useEsMovil from '../../hooks/useEsMovil';
import useConteoAnimado from '../../hooks/useConteoAnimado';
import { ESTILO_TILE, BadgeTendencia } from './widgetsUI';

// Estilos responsivos para móvil
const estilosResponsivos = `
  @media (max-width: 768px) {
    .pulso-widget-container {
      margin: 15px 0 !important;
    }

    .pulso-header {
      flex-direction: column !important;
      align-items: center !important;
      text-align: center;
      margin-bottom: 12px !important;
    }

    .pulso-header h3 {
      font-size: 18px !important;
      margin: 0 !important;
    }

    .pulso-header small {
      font-size: 11px !important;
    }

    .pulso-header-der {
      width: 100%;
      flex-direction: column !important;
      gap: 8px !important;
    }

    .btn-abrir-ads {
      width: 100% !important;
    }

    .pulso-tiles {
      grid-template-columns: 1fr 1fr !important;
      gap: 8px !important;
    }

    .pulso-tile {
      padding: 10px !important;
    }

    .pulso-viz-grid {
      grid-template-columns: 1fr !important;
      gap: 10px !important;
    }

    .viz-card-pulso {
      padding: 12px !important;
    }

    .pulso-bar-label {
      font-size: 8px !important;
    }
  }
`;

const formatearMoneda = (monto) => {
  return 'Q ' + parseFloat(monto).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const formatearHora = (timestamp) => {
  if (!timestamp) return '';
  try {
    let fecha;
    if (timestamp.toDate && typeof timestamp.toDate === 'function') fecha = timestamp.toDate();
    else if (timestamp.seconds) fecha = new Date(timestamp.seconds * 1000);
    else fecha = new Date(timestamp);
    if (isNaN(fecha.getTime())) return '';
    return fecha.toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'America/Guatemala' });
  } catch (e) {
    return '';
  }
};

// Cuándo se armó el resumen. Antes se mostraba SOLO la hora: si el cron dejaba de correr, el
// recuadro seguía diciendo "Actualizado 8:30 a. m." y parecía al día. Ahora, si no es de hoy, va
// también el día; y si pasaron más de 13 h, se avisa en ámbar. El cron corre 8:30, 2:30 y 8:30 pm:
// el hueco normal más largo es de 12 h (de 8:30 pm a 8:30 am), así que 13 h ya es un atraso real.
const HORAS_SIN_ACTUALIZAR = 13;
const fechaGT = (d) => new Intl.DateTimeFormat('es-GT', { timeZone: 'America/Guatemala', day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
export const infoActualizacion = (timestamp, ahora = new Date()) => {
  if (!timestamp) return null;
  try {
    let fecha;
    if (timestamp.toDate && typeof timestamp.toDate === 'function') fecha = timestamp.toDate();
    else if (timestamp.seconds) fecha = new Date(timestamp.seconds * 1000);
    else fecha = new Date(timestamp);
    if (isNaN(fecha.getTime())) return null;
    return {
      hora: formatearHora(fecha),
      dia: fechaGT(fecha),
      esDeHoy: fechaGT(fecha) === fechaGT(ahora),
      atrasado: ahora.getTime() - fecha.getTime() > HORAS_SIN_ACTUALIZAR * 3600000
    };
  } catch (e) {
    return null;
  }
};

// Línea "Actualizado …" del encabezado (exportada también para poder mirarla sola con
// `npm run ver-componente`, que no corre la carga de datos del widget). `ahora` es solo para
// pruebas: sin él, se compara contra la hora real.
export function LineaActualizacion({ actualizadoEn, ahora }) {
  const act = infoActualizacion(actualizadoEn, ahora);
  if (!act) return null;
  return (
    <small
      title={act.atrasado
        ? 'Este resumen se arma solo 3 veces al día (8:30, 2:30 y 8:30 pm) y lleva más de 13 horas sin actualizarse: el proceso automático no está corriendo y los números de abajo pueden estar viejos.'
        : 'El cron consolida gasto contable, vigilancia de stock y token Meta 3 veces al día (8:30, 2:30 y 8:30 pm)'}
      style={{
        color: act.atrasado ? 'var(--color-advertencia-texto)' : 'var(--color-texto-secundario)',
        fontWeight: act.atrasado ? 600 : undefined,
        fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '5px'
      }}
    >
      <span style={{ display: 'inline-flex' }}>
        {act.atrasado ? <ExclamationTriangleIcon className="w-3 h-3" /> : <ClockIcon className="w-3 h-3" />}
      </span>
      {act.atrasado
        ? `Sin actualizar desde el ${act.dia}, ${act.hora}`
        : `Actualizado ${act.esDeHoy ? '' : `el ${act.dia}, `}${act.hora} · 3 veces al día`}
    </small>
  );
}

// Semáforo del checklist
const NIVEL_CHECK = {
  ok: { Icon: CheckCircleIcon, color: 'var(--color-exito)' },
  atencion: { Icon: ExclamationTriangleIcon, color: 'var(--color-advertencia-texto)' },
  critico: { Icon: XCircleIcon, color: 'var(--color-peligro)' }
};

// Chip chiquito de plataforma (solo texto coloreado)
const COLOR_PLATAFORMA = {
  meta: 'var(--color-info-texto)',
  tiktok: 'var(--color-teal)',
  mixta: 'var(--color-texto-secundario)',
  otra: 'var(--color-texto-secundario)'
};
const NOMBRE_PLATAFORMA = { meta: 'Meta', tiktok: 'TikTok', mixta: 'Mixta', otra: '—' };

// conEnlace: el botón "Abrir Ads Dashboard" sale solo si la persona puede abrir el módulo (candado de ruta de
// Layout.js, lo decide Inicio): el permiso del widget no da el módulo.
function PulsoPublicidadWidget({ conEnlace = false }) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [itemAbierto, setItemAbierto] = useState(null);
  const [diaSeleccionado, setDiaSeleccionado] = useState(null);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'widgets_datos', 'pulso_publicidad'));
        if (vivo) setDatos(snap.exists() ? snap.data() : null);
      } catch (e) {
        console.error('Error cargando el pulso de publicidad:', e);
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => { vivo = false; };
  }, []);

  const isMobile = useEsMovil();

  // Conteos animados (los hooks van antes de cualquier return)
  const gasto = datos?.gasto || {};
  const ultimoDia = gasto.ultimoDia || null;
  const mesAnimado = useConteoAnimado(gasto.mesTotal || 0);
  const ultimoDiaAnimado = useConteoAnimado(ultimoDia?.total || 0);
  const promedioAnimado = useConteoAnimado(gasto.promedioDiario || 0);
  const pautasAnimado = useConteoAnimado(datos?.vigilancia?.enlazadasActivas || 0);

  // Barras que se llenan al aparecer los datos
  const [barrasListas, setBarrasListas] = useState(false);
  useEffect(() => {
    if (!datos) return;
    setBarrasListas(false);
    const t = setTimeout(() => setBarrasListas(true), 60);
    return () => clearTimeout(t);
  }, [datos]);

  const tendenciaUltimo = ultimoDia && (gasto.diaPrevioTotal || 0) > 0
    ? ((ultimoDia.total - gasto.diaPrevioTotal) / gasto.diaPrevioTotal) * 100
    : null;

  const serieDias = gasto.serieDias || [];
  // En móvil solo los últimos 10 días (30 barras en ~330px no se leen ni se
  // pueden tocar — decisión de Toto); en escritorio el mes completo. Como
  // isMobile es reactivo, se ajusta solo al rotar el teléfono.
  const serieVisible = isMobile ? serieDias.slice(-10) : serieDias;
  const maxDia = Math.max(...serieVisible.map((d) => d.total), 1);
  const topCuentas = gasto.topCuentas || [];
  const maxCuenta = Math.max(...topCuentas.map((c) => c.monto), 1);
  const checklist = datos?.checklist || [];

  // Día seleccionado en la gráfica (por defecto el último registrado, para
  // que en móvil siempre se vea un detalle sin depender del hover)
  const diaSel = diaSeleccionado != null
    ? diaSeleccionado
    : (serieVisible.length > 0 ? serieVisible[serieVisible.length - 1].dia : null);
  const detalleSel = serieDias.find((d) => d.dia === diaSel) || null;

  // '2026-07-06' → '06/07/2026'
  const fechaUltimo = ultimoDia?.fecha
    ? ultimoDia.fecha.split('-').reverse().join('/')
    : '';

  if (cargando) {
    // Skeleton con la silueta del widget
    return (
      <>
        <style>{estilosResponsivos}</style>
        <div className="pulso-widget-container" style={{ maxWidth: '1200px', margin: isMobile ? '15px 0' : '30px auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: isMobile ? '12px' : '16px' }}>
            <div className="skeleton" style={{ width: '240px', height: '24px', borderRadius: 'var(--radio-sm)' }} />
            <div className="skeleton" style={{ width: '280px', height: '34px', borderRadius: 'var(--radio-sm)' }} />
          </div>
          <div className="pulso-tiles" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))', gap: isMobile ? '8px' : '14px', marginBottom: '14px' }}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="skeleton" style={{ height: '84px', borderRadius: 'var(--radio-lg)' }} />
            ))}
          </div>
          <div className="pulso-viz-grid" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: isMobile ? '10px' : '14px', marginBottom: '14px' }}>
            {[0, 1].map((i) => (
              <div key={i} className="skeleton" style={{ height: '160px', borderRadius: 'var(--radio-lg)' }} />
            ))}
          </div>
          <div className="skeleton" style={{ width: '100%', height: '120px', borderRadius: 'var(--radio-lg)' }} />
        </div>
      </>
    );
  }

  // El cron 3×/día aún no armó el doc
  if (!datos) return null;

  return (
    <>
      <style>{estilosResponsivos}</style>
      <div className="pulso-widget-container" style={{ maxWidth: '1200px', margin: isMobile ? '15px 0' : '30px auto' }}>
        {/* Header estilo dashboard: título a la izquierda, metadata + acceso a la
            derecha (en móvil el CSS lo apila y centra) */}
        <div className="pulso-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px',
          marginBottom: isMobile ? '12px' : '16px'
        }}>
          <h3 style={{
            margin: 0,
            fontSize: isMobile ? '18px' : '22px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span style={{ color: 'var(--color-primario-fuerte)', display: 'inline-flex' }}><MegaphoneIcon className="w-6 h-6" /></span>
            Pulso de Publicidad
          </h3>
          <div className="pulso-header-der" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <LineaActualizacion actualizadoEn={datos.actualizadoEn} />
            {conEnlace && (
            <Link
              to="/ads-dashboard"
              className="btn-abrir-ads btn-press hover-bright"
              style={{
                height: '38px',
                boxSizing: 'border-box',
                padding: '0 20px',
                backgroundColor: 'var(--color-primario)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radio-sm)',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                textDecoration: 'none'
              }}
            >
              <ArrowTrendingUpIcon className="w-4 h-4" /> Abrir Ads Dashboard
            </Link>
            )}
          </div>
        </div>

        {/* Fila de KPI tiles */}
        <div className="pulso-tiles stagger-list" style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))',
          gap: isMobile ? '8px' : '14px',
          marginBottom: isMobile ? '10px' : '14px'
        }}>
          <div className="card pulso-tile" style={ESTILO_TILE} title="Gasto publicitario contable acumulado del mes (Meta + TikTok)">
            <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Gasto del mes</div>
            <div style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--color-texto)', fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word' }}>
              {formatearMoneda(mesAnimado)}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px' }}>
              <span style={{ color: COLOR_PLATAFORMA.meta, fontWeight: 'bold' }}>Meta</span> {formatearMoneda(gasto.mesMeta || 0)} · <span style={{ color: COLOR_PLATAFORMA.tiktok, fontWeight: 'bold' }}>TikTok</span> {formatearMoneda(gasto.mesTikTok || 0)}
            </div>
          </div>

          <div className="card pulso-tile" style={ESTILO_TILE} title="La sincronización de las 11 pm registra el gasto del DÍA ANTERIOR (Meta/TikTok siguen cobrando al día siguiente), por eso el último día completo es antier">
            <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Último día registrado</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--color-texto)', fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word' }}>
                {ultimoDia ? formatearMoneda(ultimoDiaAnimado) : '—'}
              </span>
              <BadgeTendencia
                pct={tendenciaUltimo}
                etiqueta="vs día previo"
                textoIgual="igual al día previo"
                titulo="Gasto del último día registrado comparado contra el día anterior"
              />
            </div>
            <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px' }}>
              {ultimoDia ? fechaUltimo : 'aún sin días registrados este mes'}
            </div>
          </div>

          <div className="card pulso-tile" style={ESTILO_TILE} title="Promedio de gasto por día (días completos del mes) y la proyección si se mantiene el ritmo">
            <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Ritmo diario</div>
            <div style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--color-texto)', fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word' }}>
              {formatearMoneda(promedioAnimado)}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px' }}>
              proyección del mes: {formatearMoneda(gasto.proyeccionMes || 0)}
            </div>
          </div>

          <div className="card pulso-tile" style={ESTILO_TILE} title="Campañas activas enlazadas a productos en la Vigilancia de stock">
            <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Pautas vigiladas</div>
            <div style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--color-texto)', fontVariantNumeric: 'tabular-nums' }}>
              {Math.round(pautasAnimado)}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px' }}>
              {datos.vigilancia?.productosEnPauta || 0} productos en pauta
            </div>
          </div>
        </div>

        {/* Visualizaciones: gasto diario + top cuentas */}
        <div className="pulso-viz-grid stagger-list" style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
          gap: isMobile ? '10px' : '14px',
          marginBottom: isMobile ? '10px' : '14px'
        }}>
          {/* Barras: gasto por día del mes */}
          <div className="card viz-card-pulso" style={{ ...ESTILO_TILE, display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--color-texto-secundario)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ display: 'inline-flex' }}><ChartBarIcon className="w-4 h-4" /></span> Gasto diario del mes{isMobile && serieDias.length > 10 ? ' (últimos 10 días)' : ''}
            </div>
            {serieVisible.length > 0 ? (
              <>
              <div style={{ display: 'flex', gap: '3px', flex: 1 }}>
                {serieVisible.map((d) => {
                  const pctAltura = Math.max((d.total / maxDia) * 100, d.total > 0 ? 3 : 1.5);
                  const seleccionado = d.dia === diaSel;
                  return (
                    <div key={d.dia} onClick={() => setDiaSeleccionado(d.dia)} style={{ flex: 1, minWidth: 0, textAlign: 'center', display: 'flex', flexDirection: 'column', cursor: 'pointer' }} title={`Día ${d.dia}: ${formatearMoneda(d.total)} (Meta ${formatearMoneda(d.meta)} · TikTok ${formatearMoneda(d.tiktok)})`}>
                      <div style={{ flex: 1, minHeight: '78px', display: 'flex', alignItems: 'flex-end' }}>
                        <div style={{
                          width: '100%',
                          height: `${pctAltura}%`,
                          backgroundColor: seleccionado ? 'var(--color-primario-fuerte)' : 'var(--color-primario)',
                          borderRadius: '3px 3px 0 0',
                          transform: barrasListas ? 'scaleY(1)' : 'scaleY(0)',
                          transformOrigin: 'bottom',
                          transition: 'transform 0.6s ease'
                        }} />
                      </div>
                      <div className="pulso-bar-label" style={{
                        fontSize: '9px',
                        marginTop: '3px',
                        color: seleccionado ? 'var(--color-primario-texto)' : 'var(--color-texto-secundario)',
                        fontWeight: seleccionado ? 'bold' : 'normal'
                      }}>
                        {d.dia}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize: '10px', color: 'var(--color-texto-secundario)', marginTop: '2px' }}>Día</div>
              {detalleSel && (
                <div style={{
                  marginTop: '8px',
                  padding: '6px 10px',
                  backgroundColor: 'var(--color-primario-suave)',
                  border: '1px solid var(--color-primario-borde)',
                  borderRadius: 'var(--radio-sm)',
                  fontSize: '12px',
                  textAlign: 'center',
                  fontVariantNumeric: 'tabular-nums'
                }}>
                  <strong>Día {detalleSel.dia}:</strong> {formatearMoneda(detalleSel.total)} · <span style={{ color: COLOR_PLATAFORMA.meta, fontWeight: 'bold' }}>Meta</span> {formatearMoneda(detalleSel.meta)} · <span style={{ color: COLOR_PLATAFORMA.tiktok, fontWeight: 'bold' }}>TikTok</span> {formatearMoneda(detalleSel.tiktok)}
                </div>
              )}
              </>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: 'var(--color-texto-secundario)' }}>
                Todavía no hay días completos este mes
              </div>
            )}
          </div>

          {/* Top cuentas del mes */}
          <div className="card viz-card-pulso" style={{ ...ESTILO_TILE, display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--color-texto-secundario)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ display: 'inline-flex' }}><BuildingOfficeIcon className="w-4 h-4" /></span> Top cuentas del mes
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, justifyContent: 'center' }}>
              {topCuentas.length > 0 ? topCuentas.map((c) => (
                <div key={c.nombre}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '6px', fontSize: '12px', marginBottom: '3px' }}>
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.nombre} <span style={{ color: COLOR_PLATAFORMA[c.plataforma] || 'var(--color-texto-secundario)', fontWeight: 'bold', fontSize: '10px' }}>{NOMBRE_PLATAFORMA[c.plataforma] || ''}</span>
                    </span>
                    <strong style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatearMoneda(c.monto)}</strong>
                  </div>
                  <div style={{ width: '100%', height: '6px', backgroundColor: 'var(--color-borde)', borderRadius: 'var(--radio-sm)', overflow: 'hidden' }}>
                    <div style={{
                      width: barrasListas ? `${(c.monto / maxCuenta) * 100}%` : '0%',
                      height: '100%',
                      backgroundColor: c.plataforma === 'tiktok' ? 'var(--color-teal)' : 'var(--color-info)',
                      transition: 'width 0.8s ease'
                    }} />
                  </div>
                </div>
              )) : (
                <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', textAlign: 'center' }}>
                  Sin gasto registrado este mes
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Checklist de optimización — semáforo automático, ítems expandibles */}
        <div className="card viz-card-pulso" style={ESTILO_TILE}>
          <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--color-texto-secundario)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ display: 'inline-flex' }}><CheckCircleIcon className="w-4 h-4" /></span> Checklist de optimización
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {checklist.map((item, i) => {
              const cfg = NIVEL_CHECK[item.nivel] || NIVEL_CHECK.ok;
              const Icono = cfg.Icon;
              const expandible = Array.isArray(item.detalle) && item.detalle.length > 0;
              const abierto = itemAbierto === item.id;
              return (
                <div key={item.id} style={{ borderTop: i > 0 ? '1px solid var(--color-borde-suave)' : 'none' }}>
                  <div
                    onClick={expandible ? () => setItemAbierto(abierto ? null : item.id) : undefined}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 0',
                      fontSize: '13px',
                      cursor: expandible ? 'pointer' : 'default'
                    }}
                  >
                    <span style={{ color: cfg.color, display: 'inline-flex', flexShrink: 0 }}><Icono className="w-4 h-4" /></span>
                    <span style={{ flex: 1, minWidth: 0, fontWeight: item.nivel !== 'ok' ? 'bold' : 'normal', color: item.nivel === 'critico' ? 'var(--color-peligro-texto)' : item.nivel === 'atencion' ? 'var(--color-advertencia-texto)' : 'var(--color-texto)' }}>
                      {item.titulo}
                    </span>
                    {expandible && (
                      <span style={{ color: 'var(--color-texto-secundario)', display: 'inline-flex', transform: abierto ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }}>
                        <ChevronDownIcon className="w-4 h-4" />
                      </span>
                    )}
                  </div>
                  {expandible && abierto && (
                    <div style={{ padding: '0 0 10px 26px', fontSize: '12px', color: 'var(--color-texto-secundario)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {item.detalle.map((linea, j) => (
                        <div key={j}>• {linea}</div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

export default PulsoPublicidadWidget;
